import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { trackEvent } from "@/lib/analytics";

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const current = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES.find(l => i18n.language?.startsWith(l.code)) || SUPPORTED_LANGUAGES[0];

  const change = (code) => {
    const from = (current && current.code) || "en";
    if (code !== from) {
      trackEvent("Language Switch", { from, to: code });
    }
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="btn-pill inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#1F2A24] hover:text-[#5B8C66] border border-[#DED7C7] hover:border-[#5B8C66] transition"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-switcher-button"
      >
        <Globe size={13} strokeWidth={2}/>
        <span>{current.code.toUpperCase()}</span>
      </button>
      {open && (
        <ul
          className="absolute right-0 mt-2 w-44 bg-white border border-[#DED7C7] rounded-2xl shadow-lg z-50 overflow-hidden"
          role="listbox"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <li key={l.code}>
              <button
                onClick={() => change(l.code)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-[#E9F0E0] transition ${current.code === l.code ? "font-bold text-[#3F6147] bg-[#E9F0E0]/60" : "text-[#1F2A24]"}`}
                data-testid={`language-option-${l.code}`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-base">{l.flag}</span>
                  {l.label}
                </span>
                {current.code === l.code && <Check size={14} strokeWidth={2.4} className="text-[#5B8C66]"/>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
